<template>
    <div class="analytics-ticker" v-if="items.length">
        <div
            v-for="(item, index) in items"
            :key="item.key || index"
            class="ticker-item"
        >
            <span class="ticker-label">{{ item.label }}</span>
            <span
                class="ticker-value"
                :class="{ updating: item.updating }"
                :style="item.color ? { color: item.color } : {}"
            >
                {{ formatValue(item.value, item.format) }}
            </span>
            <span v-if="item.unit" class="ticker-unit">{{ item.unit }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'LiveTicker',
    props: {
        // Static items array: [{ label, value, unit, format, color }]
        items: {
            type: Array,
            default: () => []
        },
        // API endpoint for polling
        apiUrl: {
            type: String,
            default: null
        },
        // Polling interval in ms (if using API)
        refreshInterval: {
            type: Number,
            default: 5000
        },
        // WebSocket URL for real-time updates
        wsUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            localItems: [],
            ws: null,
            pollTimer: null
        };
    },
    computed: {
        displayItems() {
            return this.localItems.length ? this.localItems : this.items;
        }
    },
    mounted() {
        if (this.wsUrl) {
            this.connectWebSocket();
        } else if (this.apiUrl) {
            this.fetchData();
            this.pollTimer = setInterval(this.fetchData, this.refreshInterval);
        }
    },
    beforeUnmount() {
        if (this.ws) {
            this.ws.close();
        }
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
        }
    },
    methods: {
        formatValue(value, format) {
            if (value === null || value === undefined) return '—';

            switch (format) {
                case 'number':
                    return Number(value).toLocaleString();
                case 'compact':
                    return Number(value).toLocaleString(undefined, {
                        notation: 'compact',
                        maximumFractionDigits: 1
                    });
                case 'currency':
                    return '$' + Number(value).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });
                case 'percent':
                    return Number(value).toFixed(1) + '%';
                case 'decimal':
                    return Number(value).toFixed(2);
                default:
                    return value;
            }
        },

        async fetchData() {
            if (!this.apiUrl) return;
            try {
                const response = await axios.get(this.apiUrl);
                this.updateItems(response.data);
            } catch (error) {
                console.error('LiveTicker fetch error:', error);
            }
        },

        connectWebSocket() {
            if (!this.wsUrl) return;

            this.ws = new WebSocket(this.wsUrl);

            this.ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                this.updateItems(data);
            };

            this.ws.onclose = () => {
                // Reconnect after 5s
                setTimeout(() => this.connectWebSocket(), 5000);
            };

            this.ws.onerror = (error) => {
                console.error('LiveTicker WebSocket error:', error);
            };
        },

        updateItems(data) {
            // Flash animation on update
            if (Array.isArray(data)) {
                this.localItems = data.map((item, index) => {
                    const existing = this.localItems[index];
                    const changed = existing && existing.value !== item.value;
                    return { ...item, updating: changed };
                });
            }

            // Remove animation class after delay
            setTimeout(() => {
                this.localItems = this.localItems.map(item => ({
                    ...item,
                    updating: false
                }));
            }, 300);
        }
    }
};
</script>
